import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import api from '../../lib/api';
import { Bell } from 'lucide-react';

// Notification bell with unread count. Owns its own 60s polling lifecycle:
// a single interval is created on mount and cleared on unmount (or when the
// fetch callback identity changes — same as the original layout behavior).
export function AlertBell({ user, globalAcademicYearId }) {
  const navigate = useNavigate();
  const [unreadAlerts, setUnreadAlerts] = useState(0);

  const showAlertBell = ['super_admin', 'tenant_admin', 'faculty', 'student', 'parent'].includes(user?.role);

  const fetchUnreadCount = useCallback(async () => {
    if (!showAlertBell) return;
    try {
      if (user?.role === 'student') {
        // Combine low-attendance alerts + in-app notifications (elective events etc.)
        const [alertsRes, inboxRes] = await Promise.allSettled([
          api.get('/alerts/my-count'),
          api.get('/notifications/inbox/count'),
        ]);
        const alertCount = alertsRes.status === 'fulfilled' ? (alertsRes.value.data.unread_count || 0) : 0;
        const inboxCount = inboxRes.status === 'fulfilled' ? (inboxRes.value.data.unread_count || 0) : 0;
        setUnreadAlerts(alertCount + inboxCount);
      } else if (user?.role === 'parent' || user?.role === 'faculty') {
        const { data } = await api.get('/notifications/inbox/count');
        setUnreadAlerts(data.unread_count || 0);
      } else {
        const params = globalAcademicYearId ? `?academic_year_id=${globalAcademicYearId}` : '';
        const { data } = await api.get(`/alerts/low-attendance/unread-count${params}`);
        setUnreadAlerts(data.unread_count || 0);
      }
    } catch { }
  }, [showAlertBell, globalAcademicYearId, user?.role]);

  useEffect(() => {
    fetchUnreadCount();
    const id = setInterval(fetchUnreadCount, 60000); // poll every 60s
    return () => clearInterval(id);
  }, [fetchUnreadCount]);

  if (!showAlertBell) return null;

  return (
    <Button
      variant="outline"
      size="icon"
      className="relative bg-white border-border"
      onClick={() => {
        if (user?.role === 'student') navigate('/dashboard/my-alerts');
        else if (user?.role === 'parent') navigate('/dashboard/parent-inbox');
        else if (user?.role === 'faculty') navigate('/dashboard/faculty/inbox');
        else navigate('/dashboard/attendance-alerts');
        fetchUnreadCount();
      }}
      data-testid="notification-bell-btn"
    >
      <Bell className="w-4 h-4 text-foreground" />
      {unreadAlerts > 0 && (
        <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid="notification-badge">
          {unreadAlerts > 99 ? '99+' : unreadAlerts}
        </span>
      )}
    </Button>
  );
}
